<template>
    <div class="new-item">
        <div class="new-item-meta">
            <span class="new-item-version" v-if="ribbonText">{{ ribbonText }}</span>
            <span class="new-item-date" v-if="date">
                <span class="new-item-date__icon">📅</span>
                {{ date }}
            </span>
        </div>
        <div class="news-content">
            <div class="news-text">
                <h3 class="new-item-title">{{ title }}</h3>
                <div class="new-item-ticket" v-if="ticket">{{ ticket }}</div>
                <div class="new-item-description">
                    <div class="new-item-description__body" v-html="description"></div>
                    <button class="link-btn" @click="clickLine" v-if="newsLink">
                        {{ newsLink }}
                        <span class="link-btn__arrow">↗</span>
                    </button>
                </div>
            </div>
            <div class="news-media" v-if="mediaUrl">
                <img v-if="isImage" :src="mediaUrl" :alt="$t('message.newsMediaAlt')" class="media-content" />
                <video v-else-if="isVideo" :src="mediaUrl" autoplay muted loop class="media-content"></video>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ItemNewsModal",
    props: {
        title: {
            type: String,
            default: "",
        },
        ticket: {
            type: String,
            default: "",
        },
        description: {
            type: String,
            default: "",
        },
        newsLink: {
            type: String,
            default: "",
        },
        date: {
            type: String,
            default: "",
        },
        linkText: {
            type: String,
            default: "",
        },
        ribbonText: {
            type: String,
            default: null,
        },
        onClicked: {
            type: Function,
            default: () => {},
        },
        onClick: {
            type: Function,
        },
        mediaUrl: {
            type: String,
            default: "",
        },
    },
    computed: {
        isImage() {
            if (!this.mediaUrl) return false;
            return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(this.mediaUrl);
        },
        isVideo() {
            if (!this.mediaUrl) return false;
            return /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i.test(this.mediaUrl);
        },
    },
    methods: {
        clickLine() {
            if (typeof this.onClick === "function") {
                this.onClick();
            } else {
                const targetURL = `/${this.linkText}`;
                this.$router.push(targetURL);
            }
            this.onClicked();
        },
    },
};
</script>

<style lang="scss" scoped>
$brand-primary: #faa566;
$brand-primary-dark: #f28c3e;
$brand-primary-soft: #fff4e8;
$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #9ca3af;
$surface: #ffffff;
$border: #eef0f3;

.new-item {
    padding: 18px 20px;
    margin: 0 0 12px;
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:last-child {
        margin-bottom: 0;
    }

    &:hover {
        border-color: #e0e3e8;
        box-shadow: 0 6px 18px rgba(17, 24, 39, 0.06);
    }
}

.new-item-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.new-item-version {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: $brand-primary-soft;
    color: $brand-primary-dark;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.01em;
    border-radius: 999px;
    border: 1px solid rgba($brand-primary, 0.25);
    white-space: nowrap;
}

.new-item-date {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 500;
    color: $text-muted;

    &__icon {
        font-size: 12px;
    }
}

.news-content {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.news-text {
    flex: 1;
    min-width: 0;
}

.news-media {
    flex: 0 0 auto;
    max-width: 200px;
}

.media-content {
    width: 100%;
    height: auto;
    border-radius: 10px;
    border: 1px solid $border;
    display: block;
}

.new-item-title {
    margin: 0 0 4px;
    font-weight: 600;
    font-size: 15px;
    line-height: 1.4;
    color: $text-primary;
    letter-spacing: -0.005em;
}

.new-item-ticket {
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 500;
    color: $text-muted;
    line-height: 1.5;
}

.new-item-description {
    font-size: 13px;
    line-height: 1.65;
    color: $text-secondary;

    &__body {
        ::v-deep p {
            margin: 0 0 6px;
        }

        ::v-deep b,
        ::v-deep strong {
            color: $text-primary;
            font-weight: 600;
        }
    }
}

.link-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 6px 12px;
    background: transparent;
    color: $brand-primary-dark;
    border: 1px solid rgba($brand-primary, 0.35);
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: $brand-primary-soft;
        border-color: $brand-primary;

        .link-btn__arrow {
            transform: translate(2px, -2px);
        }
    }

    &__arrow {
        transition: transform 0.2s ease;
        font-size: 12px;
    }
}

@media (max-width: 600px) {
    .new-item {
        padding: 16px;
    }

    .news-content {
        flex-direction: column;
        gap: 12px;
    }

    .news-media {
        max-width: 100%;
        width: 100%;
    }
}
</style>
